import { useNavigate } from 'react-router';
import DemonFace from './DemonFace';
import { DemonLogoSizes } from '../../utils/difficultyToImgSrc';
import Copy from '../ui/Copy';
import { IDMapper } from '../../utils/IDMapper';
import { useApp } from '../../context/app/useApp';
import YesTick from '../images/YesTick';
import './GridLevel.css';
import { DotsVerticalRounded } from '@boxicons/react';
import type { Difficulty, Rarity } from '@gdladder/shared/enums';
import { decodeDate } from '../../utils/decodeDate';

interface GridProps {
    ID: number;
    rating: number | null;
    enjoyment: number | null;
    proof?: string | null;
    name: string;
    creator?: string | null;
    difficulty: Difficulty;
    rarity: Rarity;
    completed?: boolean;
    inPack?: boolean;
    date?: string;
    selected?: boolean;
    position?: number;
    tooltip?: string;
    onContextMenu?: (e: React.MouseEvent) => void;
    type?: 'level' | 'submission';
}

export function GridLevel({
    ID,
    rating,
    enjoyment,
    proof,
    name,
    creator,
    difficulty,
    rarity,
    completed = false,
    inPack = false,
    date,
    selected = false,
    position,
    tooltip,
    onContextMenu,
    type = 'level',
}: GridProps) {
    const app = useApp();
    const navigate = useNavigate();

    const roundedRating = rating !== null ? Math.round(rating) : 0;
    const roundedEnjoyment = enjoyment !== null ? Math.round(enjoyment) : -1;

    return (
        <div
            className={
                'grid-level relative group min-h-40 cursor-pointer round:rounded-xl slow-effect-transition' +
                (selected ? ' outline' : '')
            }
            onContextMenu={onContextMenu}
            title={tooltip}
            onClick={() => void navigate('/level/' + ID)}
            role='link'
            tabIndex={0}
            style={{
                backgroundImage: app.enableLevelThumbnails
                    ? `linear-gradient(rgba(0, 0, 0, var(--image-opacity)), rgba(0, 0, 0, var(--image-opacity))), url("https://levelthumbs.prevter.me/thumbnail/${IDMapper(ID)}/small")`
                    : undefined,
            }}
        >
            <div
                className={
                    'relative z-10 p-2 ' +
                    (app.enableLevelThumbnails
                        ? 'text-white'
                        : 'text-theme-text round:rounded-xl ' +
                          (completed && app.highlightCompleted
                              ? 'bg-linear-to-br from-green-600 via-green-500 to-green-600'
                              : 'bg-theme-600'))
                }
            >
                <div className='relative flex justify-between'>
                    <div className='text-shadow-lg'>
                        <p>
                            <b
                                className={
                                    'text-xl wrap-anywhere ' +
                                    (completed && app.highlightCompleted && app.enableLevelThumbnails
                                        ? ' text-green-400 text-shadow-green-200/20'
                                        : '')
                                }
                            >
                                <Copy text={ID.toString()} /> <span>{name}</span>
                            </b>
                            {completed && app.highlightCompleted && (
                                <YesTick className='inline-block ms-1 mb-1 size-6' />
                            )}
                        </p>
                        {creator !== undefined && <p>by {creator ?? '(-)'}</p>}
                        {position !== undefined && <p>#{position}</p>}
                    </div>
                    <div className='self-center'>
                        <DemonFace diff={difficulty} rarity={rarity} size={DemonLogoSizes.MEDIUM} />
                    </div>
                    {onContextMenu && (
                        <div className='absolute right-0 top-0'>
                            <button
                                type='button'
                                className='block hover:bg-white/20 transition-colors rounded-full float-right'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onContextMenu(e);
                                }}
                            >
                                <DotsVerticalRounded pack='filled' />
                            </button>
                        </div>
                    )}
                </div>
                <div className='flex justify-between'>
                    <div className='flex gap-1 items-center'>
                        <p>
                            <b className={`px-2 py-1 text-xl shadow round:rounded tier-${roundedRating}`}>
                                {rating?.toFixed() ?? '-'}
                            </b>
                        </p>
                        <p>
                            <b className={`px-2 py-1 text-xl shadow round:rounded enj-${roundedEnjoyment}`}>
                                {enjoyment?.toFixed() ?? '-'}
                            </b>
                        </p>
                        {proof && (
                            <a href={proof} target='_blank' rel='noreferrer' onClick={(e) => e.stopPropagation()}>
                                <i
                                    className='bx bxl-youtube p-1 cursor-pointer round:rounded transition-colors hover:bg-theme-500 text-xl'
                                    title={type === 'level' ? 'Showcase of the level' : 'Proof of completion'}
                                />
                            </a>
                        )}
                        {inPack && <i className='bx bx-box text-2xl' title='This level is in a pack' />}
                    </div>
                    {date && <p className='text-shadow'>{decodeDate(date).toLocaleDateString()}</p>}
                </div>
            </div>
        </div>
    );
}
